import React, { useMemo, useRef, useState } from "react";
import { motion, useAnimation } from "framer-motion";
import { ACTIVITY_FILTERS, filterActivities, CATEGORIES, categoryMatchCounts, pickActivityForCategory } from "../data/activities";
import { localizedActivities, localizedFilter, localizedCategory } from "../data/localize";
import PetalLogo from "../components/PetalLogo";
import { useApp } from "../state/AppContext";
import { RotateCw, Sparkles, ArrowLeft, MapPin, Clock, DollarSign, Users, Sparkle } from "lucide-react";
import ApplyCTA from "../components/ApplyCTA";
import IntakeForm from "../components/IntakeForm";
import { GuideDownloadCard } from "../components/GuideUnlockGate";
import { usePublicConfig } from "../lib/publicConfig";


const SEGMENTS = CATEGORIES.length; // 6
function buildSegmentPath(cx, cy, r, s, e) {
  const to = (a) => [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  const [x1, y1] = to(s), [x2, y2] = to(e);
  const large = e - s > Math.PI ? 1 : 0;
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
}

const FILTER_LABEL_KEY = { day: "filter_when", env: "filter_where", group: "filter_who", cost: "filter_cost" };

export default function ActivitiesScreen() {
  const { setScreen, t, lang, sourcePath, utmContent, utmSource, utmCampaign, utmMedium } = useApp();
  const publicCfg = usePublicConfig();
  const [enrollment, setEnrollment] = useState(null);
  const [filters, setFilters] = useState({ day: "Any", env: "Any", group: "Any", cost: "Any" });
  const [result, setResult] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const controls = useAnimation();
  const totalRotationRef = useRef(0);

  const activitiesLocalized = useMemo(() => localizedActivities(lang), [lang]);
  const eligibleCount = useMemo(() => filterActivities(filters).length, [filters]);
  const catStats = useMemo(() => categoryMatchCounts(filters), [filters]);

  const wheelSize = 300, cx = 150, cy = 150, r = 144, segmentAngle = (Math.PI * 2) / SEGMENTS;
  const segmentPaths = useMemo(() => CATEGORIES.map((cat, i) => {
    const s = -Math.PI / 2 + i * segmentAngle - segmentAngle / 2;
    const e = s + segmentAngle;
    return {
      d: buildSegmentPath(cx, cy, r, s, e),
      color: cat.color,
      labelAngle: s + segmentAngle / 2,
      index: i,
      cat,
    };
  }), []);

  const spin = async () => {
    if (spinning) return;
    if (eligibleCount === 0) return;
    setSpinning(true); setResult(null); setEnrollment(null);

    // Pick a category weighted by how many eligible activities it holds — categories with 0 matches are skipped.
    const bag = [];
    for (const c of catStats) {
      for (let k = 0; k < c.count; k++) bag.push(c.id);
    }
    const chosenCatId = bag[Math.floor(Math.random() * bag.length)];
    const chosenIdx = CATEGORIES.findIndex((c) => c.id === chosenCatId);

    const pick = pickActivityForCategory(chosenCatId, filters);
    // Swap to the localized version so the result card shows Spanish labels when active
    const localizedPick = activitiesLocalized.find((a) => a.id === pick.id) || pick;

    const targetAngle = chosenIdx * (360 / SEGMENTS);
    const extraTurns = 4 + Math.floor(Math.random() * 3);
    const nextRotation = totalRotationRef.current + extraTurns * 360 + (360 - targetAngle);
    totalRotationRef.current = nextRotation;

    // Fire-and-forget spin event log
    try {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/spin-events`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          activity_id: pick.id,
          activity_name: pick.name,
          source_path: sourcePath || "spinner",
          language: lang || "en",
          utm_content: utmContent || "",
          utm_source: utmSource || "",
          utm_campaign: utmCampaign || "",
          utm_medium: utmMedium || "",
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* never block spin on analytics */
    }

    await controls.start({ rotate: nextRotation, transition: { duration: 2.6, ease: [0.22, 1, 0.36, 1] } });
    setResult(localizedPick);
    setSpinning(false);
  };

  return (
    <div className="min-h-screen pb-28 pt-6 px-6 bg-scl-cream" data-testid="activities-screen">
      <button onClick={() => setScreen("home")} className="flex items-center gap-1 text-sm text-scl-charcoal/70 mb-3 hover:text-scl-teal min-h-[44px]" data-testid="back-to-home">
        <ArrowLeft className="w-4 h-4" /> {t("back")}
      </button>

      <div className="mb-5">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-scl-teal" />
          <span className="text-xs uppercase tracking-[0.18em] text-scl-teal font-semibold">{t("activities_kicker")}</span>
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl text-scl-charcoal font-bold leading-tight">{t("activities_title")}</h2>
        <p className="text-scl-charcoal/70 mt-1 text-[15px]">{t("activities_lead")}</p>
      </div>

      <div className="space-y-3 mb-6" data-testid="activity-filters">
        {ACTIVITY_FILTERS.map((g) => (
          <div key={g.key}>
            <div className="text-[10px] uppercase tracking-widest text-scl-charcoal/60 font-semibold mb-1.5">{t(FILTER_LABEL_KEY[g.key])}</div>
            <div className="flex gap-2 flex-wrap">
              {g.options.map((opt) => {
                const active = filters[g.key] === opt;
                return (
                  <button
                    key={opt}
                    data-testid={`filter-${g.key}-${opt}`}
                    onClick={() => setFilters((f) => ({ ...f, [g.key]: opt }))}
                    className={`px-3.5 min-h-[44px] rounded-full text-sm font-medium transition-all active:scale-95 ${active ? "bg-scl-teal text-white shadow-warm-sm" : "bg-white text-scl-charcoal/80 border border-scl-border hover:border-scl-teal"}`}
                  >
                    {localizedFilter(opt, lang)}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        <div className="text-[11px] text-scl-charcoal/60 pl-1">
          {t(eligibleCount === 1 ? "activities_count_one" : "activities_count", { n: eligibleCount })}
        </div>
      </div>

      <div className="relative mx-auto mb-6" style={{ width: wheelSize, height: wheelSize + 24 }} data-testid="activity-wheel-container">
        <div className="absolute left-1/2 -translate-x-1/2 top-0 z-20">
          <div style={{ width: 0, height: 0, borderLeft: "12px solid transparent", borderRight: "12px solid transparent", borderTop: "22px solid #2B2A28", filter: "drop-shadow(0 3px 4px rgba(0,0,0,0.15))" }} />
        </div>
        <div className="absolute inset-0 top-4 rounded-full shadow-warm" style={{ background: "#fff" }} />
        <motion.svg animate={controls} initial={{ rotate: 0 }} className="absolute top-4 left-0" width={wheelSize} height={wheelSize} viewBox={`0 0 ${wheelSize} ${wheelSize}`} style={{ transformOrigin: "50% 50%", willChange: "transform" }} data-testid="activity-wheel">
          <circle cx={cx} cy={cy} r={r + 4} fill="#2B2A28" />
          {segmentPaths.map((s) => {
            const stat = catStats.find((c) => c.id === s.cat.id);
            const dim = stat && stat.count === 0;
            return <path key={s.index} d={s.d} fill={s.color} fillOpacity={dim ? 0.28 : 1} stroke="#EFE6DD" strokeWidth="2" />;
          })}
          {segmentPaths.map((s) => {
            const labelR = r - 52;
            const x = cx + labelR * Math.cos(s.labelAngle);
            const y = cy + labelR * Math.sin(s.labelAngle);
            // Rotate the label so short text reads outward from center
            const rotDeg = (s.labelAngle * 180) / Math.PI + 90;
            const locCat = localizedCategory(s.cat, lang);
            return (
              <g key={`lbl-${s.index}`} transform={`translate(${x} ${y}) rotate(${rotDeg})`}>
                <text textAnchor="middle" dy="-4" fontFamily="Georgia, serif" fontSize="13" fontWeight="700" fill="#fff" style={{ paintOrder: "stroke", stroke: "rgba(0,0,0,0.25)", strokeWidth: 2 }}>{s.cat.emoji}</text>
                <text textAnchor="middle" dy="12" fontFamily="Inter, Arial, sans-serif" fontSize="11" fontWeight="700" fill="#fff" letterSpacing="0.5" style={{ paintOrder: "stroke", stroke: "rgba(0,0,0,0.35)", strokeWidth: 2 }}>{locCat.short.toUpperCase()}</text>
              </g>
            );
          })}
        </motion.svg>
        <div className="absolute inset-0 top-4 flex items-center justify-center z-10 pointer-events-none">
          <div className="bg-white rounded-full p-3 shadow-warm">
            <PetalLogo size={72} animated={!spinning} />
          </div>
        </div>
      </div>

      <button
        onClick={spin}
        disabled={spinning}
        data-testid="activity-spin-button"
        className="w-full h-14 rounded-full bg-scl-teal text-white font-semibold text-lg shadow-warm hover:brightness-105 active:scale-[0.98] transition-all disabled:opacity-70 flex items-center justify-center gap-2 min-h-[44px]"
      >
        <RotateCw className={`w-5 h-5 ${spinning ? "animate-spin" : ""}`} />
        {spinning ? t("spinning") : result ? t("spin_again") : t("activity_spin")}
      </button>

      {result && (
        <div className="mt-6 relative bg-white rounded-2xl overflow-hidden shadow-warm animate-pop-in" data-testid="activity-result">
          <div className="relative h-40">
            <img src={result.image} alt={result.name} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-scl-charcoal/80 via-scl-charcoal/30 to-transparent" />
            <div className="absolute bottom-3 left-4 right-4 text-white">
              {(() => {
                const cat = CATEGORIES.find((c) => c.id === result.category);
                const locCat = localizedCategory(cat, lang);
                return locCat ? (
                  <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.18em] font-bold mb-1" style={{ background: locCat.color, color: "#fff" }} data-testid="activity-category-chip">
                    <span>{locCat.emoji}</span><span>{locCat.short}</span>
                  </div>
                ) : null;
              })()}
              <div className="font-serif text-2xl font-bold leading-tight" data-testid="activity-name">{result.name}</div>
              {result.place && (
                <div className="flex items-center gap-1 text-[13px] opacity-95 mt-1" data-testid="activity-place">
                  <MapPin className="w-3.5 h-3.5" /> <span className="truncate">{result.place}</span>
                </div>
              )}
            </div>
          </div>
          {enrollment ? (
            <>
              <div className="p-5">
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <MetaChip icon={Clock} label={result.time_display || result.time} />
                  <MetaChip icon={Users} label={result.group_display || result.group} />
                  <MetaChip icon={MapPin} label={result.env_display || result.env} />
                  <MetaChip icon={DollarSign} label={result.cost_display || result.cost} />
                </div>
                <p className="text-scl-charcoal/80 text-[15px] leading-relaxed mb-4">{result.description}</p>
                {result.bonus_title && (
                  <div className="rounded-xl bg-scl-mustard/25 border border-scl-mustard/50 p-4" data-testid="activity-bonus">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-scl-charcoal font-bold mb-1">
                      <Sparkle className="w-3.5 h-3.5" /> {t("activity_bonus_label")}
                    </div>
                    <div className="font-serif text-lg text-scl-charcoal font-bold leading-tight mb-1">{result.bonus_title}</div>
                    <p className="text-[14px] text-scl-charcoal/80 leading-relaxed">{result.bonus_body}</p>
                  </div>
                )}
              </div>
              {enrollment.guide_url && (
                <div className="px-5 pb-5"><GuideDownloadCard pdfUrl={enrollment.guide_url} /></div>
              )}
            </>
          ) : (
            <IntakeForm
              guide="activity"
              publicConfig={publicCfg}
              onDone={(data) => setEnrollment(data)}
            />
          )}
        </div>
      )}

      <div className="mt-6"><ApplyCTA /></div>
    </div>
  );
}

function MetaChip({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 bg-scl-cream/70 rounded-xl px-3 py-2">
      <Icon className="w-4 h-4 text-scl-teal flex-shrink-0" />
      <span className="text-[13px] text-scl-charcoal font-medium truncate">{label}</span>
    </div>
  );
}
